import React, { useEffect } from 'react';

declare global {
    interface Window {
        ChiliPiper?: {
            submit: (
                router: string,
                formId: string,
                options?: {
                    map?: boolean;
                    lead?: Record<string, unknown>;
                    onSuccess?: () => void;
                    onError?: (error: unknown) => void;
                    onClose?: () => void;
                },
            ) => void;
        };
    }
}

const loadExternalScript = async (src: string): Promise<HTMLScriptElement> => {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = src;
        script.async = true;

        script.onload = () => {
            resolve(script);
        };

        script.onerror = (error) => {
            // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
            reject(error);
        };

        document.head.appendChild(script);
    });
};

interface ChiliPiperLoaderProps {
    formID: string;
    routerName: string;
}

type SubmissionValues = { name: string; value: string | string[] }[];

const ChiliPiperLoader: React.FC<ChiliPiperLoaderProps> = ({ formID, routerName }) => {
    useEffect(() => {
        const scriptSrc = 'https://js.chilipiper.com/marketing.js';
        const cpTenantDomain = 'videoblocks';
        const lead: { [key: string]: unknown } = {};

        loadExternalScript(scriptSrc)
            .then(() => {
                window.addEventListener(
                    'message',
                    function (
                        event: MessageEvent<{
                            type: string;
                            eventName: string;
                            id: number;
                            data: { submissionValues?: SubmissionValues } | SubmissionValues;
                        }>,
                    ) {
                        if (event.data.type === 'hsFormCallback') {
                            if (event.data.eventName === 'onFormSubmit') {
                                if (formID && event.data.id.toString() !== formID) {
                                    return; // Filter out if form ID does not match
                                }

                                const formData =
                                    (event.data.data as { submissionValues?: SubmissionValues }).submissionValues ||
                                    (event.data.data as SubmissionValues);

                                // eslint-disable-next-line @typescript-eslint/no-for-in-array
                                for (const key in formData) {
                                    let value = formData[key].value !== undefined ? formData[key].value : formData[key];
                                    if (Array.isArray(value)) {
                                        value = value.toString().replaceAll(',', ';');
                                    }
                                    lead[formData[key].name] = value;
                                }
                            } else if (event.data.eventName === 'onFormSubmitted') {
                                window.ChiliPiper!.submit(cpTenantDomain, routerName, {
                                    map: true,
                                    lead: lead,
                                });
                            }
                        }
                    },
                );
            })
            .catch((error) => {
                console.error('Error loading external script:', error);
            });
    }, []);

    return <div id="chili-piper" />;
};

export default ChiliPiperLoader;
